import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Row, Col, Table, ProgressBar, Modal } from 'react-bootstrap';
import { Doughnut, Pie, Bar } from 'react-chartjs-2';
import ReactSelect from 'react-select';
import { Fab } from 'react-tiny-fab';
import 'react-tiny-fab/dist/styles.css';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import {
    Chart as ChartJS,
    ArcElement,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Tooltip,
    Legend
} from 'chart.js';

import { Button, RangeDate, SmallBox } from '@app/components/index';
import TablaTicketService from '@app/services/tablaTicket.service';
import GrupoService from '@app/services/grupo.service';
import FechaFunction from '@app/functions/FechaFunction';

ChartJS.register(
    ArcElement,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Tooltip,
    Legend
);

// Paletas
const COLOR_TRAFFIC = {
    'A-REQUERIMIENTO': 'rgba(23, 162, 184, 0.75)',
    VERDE: 'rgba(40, 167, 69, 0.75)',
    AMARILLO: 'rgba(255, 193, 7, 0.75)',
    NARANJA: 'rgba(253, 126, 20, 0.75)',
    ROJO: 'rgba(220, 53, 69, 0.75)',
    SIN_COLOR: 'rgba(108, 117, 125, 0.75)'
};
const COLOR_TRAFFIC_BORDER = {
    'A-REQUERIMIENTO': 'rgb(23, 162, 184)',
    VERDE: 'rgb(40, 167, 69)',
    AMARILLO: 'rgb(255, 193, 7)',
    NARANJA: 'rgb(253, 126, 20)',
    ROJO: 'rgb(220, 53, 69)',
    SIN_COLOR: 'rgb(108, 117, 125)'
};
const COLOR_TRAFFIC_LABEL = {
    'A-REQUERIMIENTO': 'A REQUERIMIENTO',
    VERDE: 'VERDE',
    AMARILLO: 'AMARILLO',
    NARANJA: 'NARANJA',
    ROJO: 'ROJO',
    SIN_COLOR: 'SIN COLOR'
};
const PALETA = [
    'rgba(51, 102, 204, 0.7)',
    'rgba(220, 57, 18, 0.7)',
    'rgba(255, 153, 0, 0.7)',
    'rgba(16, 150, 24, 0.7)',
    'rgba(153, 0, 153, 0.7)',
    'rgba(0, 153, 198, 0.7)',
    'rgba(221, 68, 119, 0.7)',
    'rgba(102, 170, 0, 0.7)',
    'rgba(184, 46, 46, 0.7)',
    'rgba(49, 99, 149, 0.7)'
];

const ESTADOS_OPTS = ['ABIERTO', 'CERRADO'];
const PRIORIDAD_OPTS = ['LEVE', 'GRAVE', 'CRITICA', 'BAJA', 'A DEFINIR'];
const SEGMENTO_OPTS = ['SVV', 'SGE', 'TETRA'];
const COLOR_OPTS = ['A-REQUERIMIENTO', 'VERDE', 'AMARILLO', 'NARANJA', 'ROJO'];

const toOpts = (arr) => arr.map((v) => ({ value: v, label: v }));
const SEGMENTO_SELECT_OPTS = toOpts(SEGMENTO_OPTS);
const PRIORIDAD_SELECT_OPTS = toOpts(PRIORIDAD_OPTS);
const ESTADO_SELECT_OPTS = toOpts(ESTADOS_OPTS);
const COLOR_SELECT_OPTS = toOpts(COLOR_OPTS);
const CAPITAL_SELECT_OPTS = [
    { value: '', label: 'Ambos' },
    { value: 'true', label: 'Solo Capital' },
    { value: 'false', label: 'No Capital' }
];

const SELECT_STYLES = { menuPortal: (base) => ({ ...base, zIndex: 9999 }) };

// Rango por defecto: del 1ro del mes actual hasta hoy
const defaultRange = () => {
    const end = new Date();
    const start = new Date(end.getFullYear(), end.getMonth(), 1);
    return { startDate: start, endDate: end };
};

const DashboardMetricas = ({ user }) => {
    const navigate = useNavigate();
    const [rangoFecha, setRangoFecha] = useState(defaultRange());
    const [filtros, setFiltros] = useState({
        segmento: [],
        grupoId: [],
        prioridad: [],
        estado: [],
        color: [],
        isCapital: ''
    });
    const [grupos, setGrupos] = useState([]);
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [error, setError] = useState(null);
    const [showFiltros, setShowFiltros] = useState(false);
    const [resetKey, setResetKey] = useState(0);
    const isMounted = useRef(true);

    useEffect(() => () => { isMounted.current = false; }, []);

    useEffect(() => {
        if (user) {
            if (user && user.id_p > 9999) {
                navigate('/');
            }
        }
    }, [user, navigate]);

    // Carga inicial de catalogos
    useEffect(() => {
        GrupoService.getAll().then((r) => {
            if (!isMounted.current) return;
            setGrupos(Array.isArray(r) ? r : (r?.data || []));
        }).catch(() => setGrupos([]));
    }, []);

    const buildQuery = () => {
        const query = {
            fecIni: FechaFunction.format(rangoFecha.startDate, 'yyyy-MM-dd'),
            fecFin: FechaFunction.format(rangoFecha.endDate, 'yyyy-MM-dd')
        };
        if (filtros.segmento.length) query.segmento = filtros.segmento;
        if (filtros.grupoId.length) query.grupoId = filtros.grupoId;
        if (filtros.prioridad.length) query.prioridad = filtros.prioridad;
        if (filtros.estado.length) query.estado = filtros.estado;
        if (filtros.color.length) query.color = filtros.color;
        if (filtros.isCapital !== '' && filtros.isCapital !== null && filtros.isCapital !== undefined) {
            query.isCapital = filtros.isCapital;
        }
        return query;
    };

    const cargar = () => {
        setIsLoading(true);
        setError(null);
        TablaTicketService.dashboardMetricas(buildQuery())
            .then((resp) => {
                if (!isMounted.current) return;
                setData(resp?.data ? resp.data : resp);
            })
            .catch((err) => {
                if (!isMounted.current) return;
                setError(err?.response?.data?.msg || 'Error al cargar el dashboard');
                setData(null);
            })
            .finally(() => {
                if (isMounted.current) setIsLoading(false);
            });
    };

    const descargarExcel = () => {
        if (isExporting) return;
        setIsExporting(true);
        TablaTicketService.dashboardMetricasExcel(buildQuery())
            .then((resp) => {
                const blob = resp?.data instanceof Blob ? resp.data : new Blob([resp?.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const fecha = FechaFunction.format(new Date(), 'yyyyMMdd-HHmm');
                saveAs(blob, `dashboard-metricas-${fecha}.xlsx`);
            })
            .catch(async (err) => {
                // La respuesta de error puede venir como Blob (JSON con msg) porque pedimos
                // responseType 'blob'. El interceptor de axios a veces reenvia el data crudo,
                // asi que soportamos ambos casos.
                let msg = 'Error al descargar el excel';
                const candidate = err instanceof Blob ? err : (err?.response?.data ?? err?.data);
                if (candidate instanceof Blob) {
                    try {
                        const text = await candidate.text();
                        const parsed = JSON.parse(text);
                        msg = parsed?.msg || msg;
                    } catch (_) { /* respuesta no era JSON */ }
                } else if (candidate?.msg) {
                    msg = candidate.msg;
                }
                toast.error(msg);
            })
            .finally(() => {
                if (isMounted.current) setIsExporting(false);
            });
    };

    // Primera carga
    useEffect(() => { cargar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const onChangeFiltro = (name, value) => setFiltros((prev) => ({ ...prev, [name]: value }));

    const onChangeMulti = (name) => (selected) => {
        const arr = Array.isArray(selected) ? selected.map((o) => o.value) : [];
        setFiltros((prev) => ({ ...prev, [name]: arr }));
    };

    const gruposOptions = useMemo(
        () => grupos.map((g) => ({ value: String(g.id_g), label: g.nombre_g })),
        [grupos]
    );

    const pickSelected = (opts, values) => opts.filter((o) => values.includes(o.value));
    const pickSingle = (opts, value) => opts.find((o) => o.value === value) || null;

    const resetFiltros = () => {
        setFiltros({
            segmento: [],
            grupoId: [],
            prioridad: [],
            estado: [],
            color: [],
            isCapital: ''
        });
        setRangoFecha(defaultRange());
        setResetKey((k) => k + 1);
    };

    const activeFiltersCount = useMemo(() => {
        let count = 0;
        if (filtros.segmento.length) count += 1;
        if (filtros.grupoId.length) count += 1;
        if (filtros.prioridad.length) count += 1;
        if (filtros.estado.length) count += 1;
        if (filtros.color.length) count += 1;
        if (filtros.isCapital !== '' && filtros.isCapital !== null && filtros.isCapital !== undefined) count += 1;
        return count;
    }, [filtros]);

    const aplicarFiltros = () => {
        cargar();
        setShowFiltros(false);
    };

    // ---------- Datasets de graficos ----------
    const chartColor = useMemo(() => {
        const colors = data?.porColorTrafficTime || { 'A-REQUERIMIENTO': 0, VERDE: 0, AMARILLO: 0, NARANJA: 0, ROJO: 0, SIN_COLOR: 0 };
        const labels = Object.keys(colors);
        return {
            labels: labels.map((l) => `${COLOR_TRAFFIC_LABEL[l] || l} - ${colors[l]}`),
            datasets: [{
                data: labels.map((l) => colors[l]),
                backgroundColor: labels.map((l) => COLOR_TRAFFIC[l]),
                borderColor: labels.map((l) => COLOR_TRAFFIC_BORDER[l]),
                borderWidth: 2
            }]
        };
    }, [data]);

    const buildDataset = (items, labelKey, valueKey = 'count') => {
        const labels = items.map((it) => `${it[labelKey]} - ${it[valueKey]}`);
        const values = items.map((it) => it[valueKey]);
        const bg = values.map((_, i) => PALETA[i % PALETA.length]);
        return {
            labels,
            datasets: [{
                data: values,
                backgroundColor: bg,
                borderColor: bg.map((c) => c.replace('0.7', '1')),
                borderWidth: 2
            }]
        };
    };

    const resumenPct = useMemo(() => {
        const total = data?.resumen?.total || 1;
        const abiertos = data?.resumen?.abiertos ?? 0;
        const cerrados = data?.resumen?.cerrados ?? 0;
        return {
            abiertos: Math.round((abiertos / total) * 100),
            cerrados: Math.round((cerrados / total) * 100)
        };
    }, [data]);
    const chartEstado = useMemo(() => buildDataset(data?.porEstado || [], 'estado'), [data]);
    const chartSegmento = useMemo(() => buildDataset(data?.porSegmento || [], 'segmento'), [data]);
    const chartContrato = useMemo(() => buildDataset(data?.porContratoNubi || [], 'contrato'), [data]);
    const chartMotivo = useMemo(() => buildDataset(data?.porMotivo || [], 'motivo'), [data]);
    const chartEquipo = useMemo(() => buildDataset(data?.porEquipoCamara || [], 'nombre'), [data]);
    const chartGrupo = useMemo(() => {
        const items = data?.porGrupo || [];
        const labels = items.map((g) => `${g.nombre} (${g.ticketsAbiertos})`);
        return {
            labels,
            datasets: [
                {
                    label: 'Abiertos',
                    data: items.map((g) => g.ticketsAbiertos),
                    backgroundColor: 'rgba(75, 192, 192, 0.6)',
                    borderColor: 'rgba(75, 192, 192, 1)',
                    borderWidth: 1
                }
            ]
        };
    }, [data]);

    const barOpts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } };
    const doughOpts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'right' } } };
    const barHorizOpts = { ...barOpts, indexAxis: 'y', plugins: { legend: { display: false } } };

    return (
        <div className="container-fluid">
            {/* Encabezado */}
            <div className="row">
              <div className="col-lg-12 my-lg-12 my-12 mb-lg-3">
                <div className="col-lg-12 my-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <h2 className="text-gray-dark mt-3 user-select-none mb-0">
                    Dashboard de Metricas
                  </h2>
                  <Button
                    onClick={descargarExcel}
                    isLoading={isExporting}
                    disabled={isLoading}
                    theme="success"
                  >
                    <i className="fas fa-file-excel mr-1" />
                    {' Excel'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Modal de filtros */}
            <Modal
                show={showFiltros}
                onHide={() => setShowFiltros(false)}
                backdrop="static"
                size="lg"
                scrollable
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="fas fa-filter mr-2" />
                        Filtros del dashboard
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row className="g-2">
                        <Col md={12}>
                            <Form.Label>Rango de fecha (creacion)</Form.Label>
                            <RangeDate
                                key={resetKey}
                                minDate={new Date('2022-12-02')}
                                initianDate={rangoFecha}
                                onChange={(e) => setRangoFecha(e)}
                                isDisabled={isLoading}
                            />
                        </Col>
                        <Col md={6}>
                            <Form.Label>Segmento</Form.Label>
                            <ReactSelect
                                isMulti
                                isClearable
                                placeholder="Todos"
                                options={SEGMENTO_SELECT_OPTS}
                                value={pickSelected(SEGMENTO_SELECT_OPTS, filtros.segmento)}
                                onChange={onChangeMulti('segmento')}
                                menuPortalTarget={document.body}
                                styles={SELECT_STYLES}
                                classNamePrefix="react-select"
                            />
                        </Col>
                        <Col md={6}>
                            <Form.Label>Grupo</Form.Label>
                            <ReactSelect
                                isMulti
                                isClearable
                                placeholder="Todos"
                                options={gruposOptions}
                                value={pickSelected(gruposOptions, filtros.grupoId)}
                                onChange={onChangeMulti('grupoId')}
                                menuPortalTarget={document.body}
                                styles={SELECT_STYLES}
                                classNamePrefix="react-select"
                            />
                        </Col>
                        <Col md={6}>
                            <Form.Label>Prioridad</Form.Label>
                            <ReactSelect
                                isMulti
                                isClearable
                                placeholder="Todas"
                                options={PRIORIDAD_SELECT_OPTS}
                                value={pickSelected(PRIORIDAD_SELECT_OPTS, filtros.prioridad)}
                                onChange={onChangeMulti('prioridad')}
                                menuPortalTarget={document.body}
                                styles={SELECT_STYLES}
                                classNamePrefix="react-select"
                            />
                        </Col>
                        <Col md={6}>
                            <Form.Label>Estado</Form.Label>
                            <ReactSelect
                                isMulti
                                isClearable
                                placeholder="Todos"
                                options={ESTADO_SELECT_OPTS}
                                value={pickSelected(ESTADO_SELECT_OPTS, filtros.estado)}
                                onChange={onChangeMulti('estado')}
                                menuPortalTarget={document.body}
                                styles={SELECT_STYLES}
                                classNamePrefix="react-select"
                            />
                        </Col>
                        <Col md={6}>
                            <Form.Label>Color Traffic</Form.Label>
                            <ReactSelect
                                isMulti
                                isClearable
                                placeholder="Todos"
                                options={COLOR_SELECT_OPTS}
                                value={pickSelected(COLOR_SELECT_OPTS, filtros.color)}
                                onChange={onChangeMulti('color')}
                                menuPortalTarget={document.body}
                                styles={SELECT_STYLES}
                                classNamePrefix="react-select"
                            />
                        </Col>
                        <Col md={6}>
                            <Form.Label>Capital</Form.Label>
                            <ReactSelect
                                isClearable={false}
                                options={CAPITAL_SELECT_OPTS}
                                value={pickSingle(CAPITAL_SELECT_OPTS, filtros.isCapital)}
                                onChange={(opt) => onChangeFiltro('isCapital', opt ? opt.value : '')}
                                menuPortalTarget={document.body}
                                styles={SELECT_STYLES}
                                classNamePrefix="react-select"
                            />
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                    <Button onClick={resetFiltros} isLoading={false} theme="secondary" style={{ backgroundColor: '#6c757d', borderColor: '#6c757d' }}>
                        <i className="fas fa-eraser mr-1" />
                        {' Limpiar'}
                    </Button>
                    <div className="d-flex gap-2">
                        <Button
                            onClick={() => setShowFiltros(false)}
                            isLoading={false}
                            theme="light"
                        >
                            Cerrar
                        </Button>
                        <Button onClick={aplicarFiltros} isLoading={isLoading} theme="primary">
                            <i className="fas fa-sync-alt mr-1" />
                            {' Aplicar'}
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>

            {/* FAB para abrir el modal de filtros */}
            <Fab
                mainButtonStyles={{ backgroundColor: '#007bff' }}
                icon={(
                    <div
                      style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}
                    >
                        <i className="fas fa-filter" />
                        {activeFiltersCount > 0 && (
                            <span
                                aria-label={`${activeFiltersCount} filtros activos`}
                                style={{
                                    position: 'absolute',
                                    top: -5,
                                    right: -5,
                                    minWidth: 20,
                                    height: 20,
                                    padding: '0 5px',
                                    borderRadius: 10,
                                    backgroundColor: '#dc3545',
                                    color: '#fff',
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '2px solid #fff',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                    pointerEvents: 'none',
                                    lineHeight: 1
                                }}
                            >
                                {activeFiltersCount}
                            </span>
                        )}
                    </div>
                )}
                alwaysShowTitle
                event="click"
                onClick={() => setShowFiltros(true)}
            />

            {error && (
                <div className="alert alert-danger" role="alert">{error}</div>
            )}

            {/* Fila resumen (SmallBox) */}
            <Row className="mb-2">
                <Col lg={2} md={6}>
                    <SmallBox count={data?.resumen?.total ?? 0} title="Total tickets" type="info" icon="ion-ios-list" />
                </Col>
                <Col lg={2} md={6}>
                    <SmallBox count={data?.resumen?.abiertos ?? 0} title="Abiertos" type="success" icon="ion-android-unlock" />
                </Col>
                <Col lg={2} md={6}>
                    <SmallBox count={data?.resumen?.cerrados ?? 0} title="Cerrados" type="danger" icon="ion-android-lock" />
                </Col>
                <Col lg={2} md={6}>
                    <SmallBox count={data?.resumen?.invalidos ?? 0} title="Mal cargados" type="warning" icon="ion-alert" />
                </Col>
                <Col lg={2} md={6}>
                    <SmallBox count={data?.resumen?.reabiertos ?? 0} title="Reabiertos" type="warning" icon="ion-refresh" />
                </Col>
                <Col lg={2} md={6}>
                    <SmallBox count={data?.comentarios?.total ?? 0} title="Comentarios" type="info" icon="ion-chatbubbles" />
                </Col>
            </Row>

            {/* Resumen abiertos / cerrados */}
            <Row className="mb-2">
                <Col lg={12}>
                    <Card>
                        <Card.Body className="py-3">
                            <Row className="align-items-center">
                                <Col md={3} className="text-center">
                                    <div style={{ fontSize: '2.2rem', fontWeight: 700, color: '#28a745', lineHeight: 1 }}>
                                        {data?.resumen?.abiertos ?? 0}
                                    </div>
                                    <div className="text-muted mt-1">
                                        <strong>Abiertos</strong>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="d-flex justify-content-between mb-1">
                                        <small className="text-success">{`Abiertos — ${resumenPct.abiertos}%`}</small>
                                        <small className="text-danger">{`Cerrados — ${resumenPct.cerrados}%`}</small>
                                    </div>
                                    <ProgressBar style={{ height: '1.2rem', borderRadius: '0.5rem' }}>
                                        <ProgressBar variant="success" now={resumenPct.abiertos} key={1} />
                                        <ProgressBar variant="danger" now={resumenPct.cerrados} key={2} />
                                    </ProgressBar>
                                    <div className="text-center mt-2 text-muted">
                                        <small>{`Total: ${data?.resumen?.total ?? 0} tickets`}</small>
                                    </div>
                                </Col>
                                <Col md={3} className="text-center">
                                    <div style={{ fontSize: '2.2rem', fontWeight: 700, color: '#dc3545', lineHeight: 1 }}>
                                        {data?.resumen?.cerrados ?? 0}
                                    </div>
                                    <div className="text-muted mt-1">
                                        <strong>Cerrados</strong>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Fila 2: color, estado, segmento */}
            <Row className="mb-2">
                <Col lg={4} md={12}>
                    <Card>
                        <Card.Header><strong>Por color Traffic Time</strong></Card.Header>
                        <Card.Body style={{ height: 300 }}>
                            <Doughnut data={chartColor} options={doughOpts} />
                        </Card.Body>
                    </Card>
                </Col>
                <Col lg={4} md={12}>
                    <Card>
                        <Card.Header><strong>Por estado</strong></Card.Header>
                        <Card.Body style={{ height: 300 }}>
                            <Doughnut data={chartEstado} options={doughOpts} />
                        </Card.Body>
                    </Card>
                </Col>
                <Col lg={4} md={12}>
                    <Card>
                        <Card.Header><strong>Por segmento</strong></Card.Header>
                        <Card.Body style={{ height: 300 }}>
                            <Pie data={chartSegmento} options={doughOpts} />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Fila 3: grupos (bar) + motivos (bar horizontal) */}
            <Row className="mb-2">
                <Col lg={7} md={12}>
                    <Card>
                        <Card.Header><strong>Tickets abiertos por grupo</strong></Card.Header>
                        <Card.Body style={{ height: 360 }}>
                            <Bar data={chartGrupo} options={barOpts} />
                        </Card.Body>
                    </Card>
                </Col>
                <Col lg={5} md={12}>
                    <Card>
                        <Card.Header><strong>Top motivos</strong></Card.Header>
                        <Card.Body style={{ height: 360 }}>
                            <Bar data={chartMotivo} options={barHorizOpts} />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Fila 4: equipos/camaras + contrato (cierres) */}
            <Row className="mb-2">
                <Col lg={7} md={12}>
                    <Card>
                        <Card.Header><strong>Top equipos / camaras</strong></Card.Header>
                        <Card.Body style={{ height: 360 }}>
                            <Bar data={chartEquipo} options={barHorizOpts} />
                        </Card.Body>
                    </Card>
                </Col>
                <Col lg={5} md={12}>
                    <Card>
                        <Card.Header><strong>Cierres por contrato / situacion</strong></Card.Header>
                        <Card.Body style={{ height: 360 }}>
                            <Pie data={chartContrato} options={doughOpts} />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Fila 5: tiempo por grupo */}
            <Row className="mb-2">
                <Col lg={12}>
                    <Card>
                        <Card.Header><strong>Tiempo de tickets asignados por grupo (horas)</strong></Card.Header>
                        <Card.Body style={{ overflowX: 'auto' }}>
                            <Table striped bordered hover size="sm">
                                <thead>
                                    <tr>
                                        <th>Grupo</th>
                                        <th>Abiertos</th>
                                        <th>Promedio (h)</th>
                                        <th>Mediana (h)</th>
                                        <th>Maximo (h)</th>
                                        <th>Total (h)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data?.porGrupo || []).map((g) => (
                                        <tr key={g.grupoId}>
                                            <td>{g.nombre}</td>
                                            <td>{g.ticketsAbiertos}</td>
                                            <td>{g.tiempoHoras?.promedio}</td>
                                            <td>{g.tiempoHoras?.mediana}</td>
                                            <td>{g.tiempoHoras?.maximo}</td>
                                            <td>{g.tiempoHoras?.total}</td>
                                        </tr>
                                    ))}
                                    {(!data?.porGrupo || data.porGrupo.length === 0) && (
                                        <tr>
                                            <td colSpan={7} className="text-center text-muted">
                                                Sin datos
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Fila 6: comentarios */}
            <Row className="mb-4">
                <Col lg={4} md={12}>
                    <Card className="h-100">
                        <Card.Header><strong>Comentarios</strong></Card.Header>
                        <Card.Body>
                            <Row className="g-2 text-center mb-3">
                                <Col xs={6}>
                                    <div className="p-2 rounded" style={{ backgroundColor: 'rgba(51, 102, 204, 0.08)' }}>
                                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#3366cc', lineHeight: 1 }}>
                                            {data?.comentarios?.total ?? 0}
                                        </div>
                                        <small className="text-muted">Total</small>
                                    </div>
                                </Col>
                                <Col xs={6}>
                                    <div className="p-2 rounded" style={{ backgroundColor: 'rgba(40, 167, 69, 0.08)' }}>
                                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#28a745', lineHeight: 1 }}>
                                            {data?.comentarios?.promedioPorTicket ?? 0}
                                        </div>
                                        <small className="text-muted">Prom. / ticket</small>
                                    </div>
                                </Col>
                                <Col xs={6}>
                                    <div className="p-2 rounded" style={{ backgroundColor: 'rgba(220, 53, 69, 0.08)' }}>
                                        <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#dc3545', lineHeight: 1 }}>
                                            {data?.comentarios?.internos ?? 0}
                                        </div>
                                        <small className="text-muted">Internos</small>
                                    </div>
                                </Col>
                                <Col xs={6}>
                                    <div className="p-2 rounded" style={{ backgroundColor: 'rgba(23, 162, 184, 0.08)' }}>
                                        <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#17a2b8', lineHeight: 1 }}>
                                            {data?.comentarios?.publicos ?? 0}
                                        </div>
                                        <small className="text-muted">Publicos</small>
                                    </div>
                                </Col>
                            </Row>
                            <div className="mb-2 d-flex justify-content-between align-items-center">
                                <strong className="text-muted" style={{ fontSize: '0.85rem' }}>
                                    TOP 5 USUARIOS
                                </strong>
                            </div>
                            <Table size="sm" className="mb-0 align-middle">
                                <thead>
                                    <tr>
                                        <th>Usuario</th>
                                        <th className="text-end">Comentarios</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data?.comentarios?.topUsuarios || []).map((u, idx) => (
                                        <tr key={u.personaId ?? u.nombre}>
                                            <td>
                                                <span
                                                    className="d-inline-flex align-items-center justify-content-center me-2"
                                                    style={{
                                                        width: 22,
                                                        height: 22,
                                                        borderRadius: '50%',
                                                        backgroundColor: PALETA[idx % PALETA.length],
                                                        color: '#fff',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 700
                                                    }}
                                                >
                                                    {idx + 1}
                                                </span>
                                                {u.nombre}
                                            </td>
                                            <td className="text-end">
                                                <span className="badge bg-primary">{u.total}</span>
                                            </td>
                                        </tr>
                                    ))}
                                    {(!data?.comentarios?.topUsuarios || data.comentarios.topUsuarios.length === 0) && (
                                        <tr>
                                            <td colSpan={2} className="text-center text-muted">
                                                Sin datos
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>
                <Col lg={8} md={12}>
                    <Card className="h-100">
                        <Card.Header><strong>Top tickets con mas comentarios</strong></Card.Header>
                        <Card.Body style={{ overflowX: 'auto' }}>
                            <Table striped bordered hover size="sm">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Titulo</th>
                                        <th>Total</th>
                                        <th>Internos</th>
                                        <th>Publicos</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data?.comentarios?.topTickets || []).map((t) => (
                                        <tr key={t.ticketId}>
                                            <td>{t.ticketId}</td>
                                            <td>{t.titulo}</td>
                                            <td>{t.total}</td>
                                            <td>{t.internos}</td>
                                            <td>{t.publicos}</td>
                                        </tr>
                                    ))}
                                    {(!data?.comentarios?.topTickets || data.comentarios.topTickets.length === 0) && (
                                        <tr>
                                            <td colSpan={5} className="text-center text-muted">
                                                Sin datos
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

const mapStateToProps = (state) => ({
    user: state.auth.currentUser
});

export default connect(mapStateToProps)(DashboardMetricas);
